/**
 * Mark a Lead (CRM) or a Customer as lost — Engine v2 Phase 1.
 *
 * Shared by LeadDetail (mode="lead") and CustomerDetail (mode="customer").
 * Posts to /v1/dead-leads/mark, which creates a pool row in NEW, flips the
 * customer's lifecycle_status to DEAD_LEAD, and (for leads) closes the lead
 * as LOST with the chosen reason.
 */
import { useEffect, useState } from 'react';
import { deadLeadsService } from '../lib/services';

const REASONS = [
  ['NO_RESPONSE',        'No response after follow-ups'],
  ['LOST_TO_COMPETITOR', 'Lost to a competitor'],
  ['NOT_READY',          'Not ready / timing not right'],
  ['BAD_CONTACT',        'Bad or invalid contact details'],
  ['OTHER',              'Other'],
];

export default function MarkDeadLeadModal({ open, mode = 'lead', leadId, customerId, onClose, onMarked }) {
  const [reason, setReason] = useState(REASONS[0][0]);
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]  = useState(null);

  useEffect(() => {
    if (!open) return;
    setReason(REASONS[0][0]);
    setBusy(false);
    setError(null);
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = mode === 'lead' ? { leadId, reason } : { customerId, reason };
      const dl = await deadLeadsService.mark(body);
      onMarked?.(dl);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to mark as lost');
      setBusy(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="mdl-title"
         onClick={onClose} className="modal-backdrop">
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}
            style={{ width: 'min(460px, 92vw)' }}>
        <div className="modal-header">
          <div>
            <div id="mdl-title" style={{ fontSize: 16, fontWeight: 600 }}>
              Mark as lost
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              {mode === 'lead'
                ? 'Closes this lead as LOST and moves the customer into the lost-lead pool for reactivation.'
                : 'Moves this customer into the lost-lead pool for reactivation.'}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-field">
            <label htmlFor="mdl-reason">Reason</label>
            <select id="mdl-reason" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus>
              {REASONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          {error && <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn small danger" disabled={busy}>
            {busy ? 'Marking…' : 'Mark as lost'}
          </button>
        </div>
      </form>
    </div>
  );
}
